import { useMutation, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormPageLayout } from '@/components/layout/form-page-layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useRouteParams } from '@/contexts/RouteContext';
import type { TerminalViewModel } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useEditPageHeader } from '@/hooks/useEditPageHeader';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { GateForm } from './gate-form';
import {
	buildUpdateGateInput,
	mapGateToFormData,
	type GateFormSubmitData
} from './gate-input';
import {
	GATE_QUERY,
	TERMINAL_TYPES_QUERY,
	GATE_UPDATE_MUTATION
} from './gates.gql.tsx';

type TerminalTypeOption = {
	terminalTypeId: number;
	name?: string | null;
};

type GateRecord = TerminalViewModel;

const isValidUlid = (value: string | undefined): value is string => {
	if (!value) {
		return false;
	}

	if (value.length !== 26) {
		return false;
	}

	return /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/i.test(value);
};

export const GatesEdit = () => {
	const { t } = useTranslation();
	const go = useGo();
	const { id } = useRouteParams();
	useEditPageHeader({ id, resourceName: 'gates' });

	const {
		data: gateResponse,
		isLoading: isGateLoading,
		isError: gateError,
		error: fetchError
	} = useOne<GateRecord>({
		resource: 'gates',
		id: id ?? '',
		meta: {
			gqlQuery: GATE_QUERY,
			dataPath: 'terminal'
		},
		queryOptions: {
			enabled: Boolean(id)
		}
	});

	const gate = gateResponse?.data;

	const {
		data: terminalTypesData,
		loading: isTerminalTypeLoading,
		error: terminalTypesError
	} = useQuery<{ terminalTypes: TerminalTypeOption[] }>(TERMINAL_TYPES_QUERY);

	const terminalTypeOptions = useMemo(
		() =>
			(terminalTypesData?.terminalTypes ?? [])
				.filter(type => type?.terminalTypeId != null)
				.map(type => ({
					value: String(type.terminalTypeId),
					label: type.name ?? `Type ${type.terminalTypeId}`
				})),
		[terminalTypesData]
	);

	const [updateGate, { loading: isSubmitting }] = useMutation(
		GATE_UPDATE_MUTATION
	);

	// Memoize initial form data to prevent unnecessary form resets
	// Must be called before any conditional returns (Rules of Hooks)
	const initialFormData = useMemo(
		() => (gate ? mapGateToFormData(gate) : undefined),
		[gate]
	);

	const handleSubmit = async (formData: GateFormSubmitData) => {
		if (!gate?.terminalId) {
			showMutationErrorToast('gates', 'update', { message: 'Gate identifier is missing.' });
			return;
		}

		const payload = buildUpdateGateInput(formData, gate.terminalId);

		try {
			const { data } = await updateGate(
				getMutationOptions('gates', { input: payload })
			);

			const message = extractCheckResultError(data?.updateTerminal);

			if (message) {
				showMutationErrorToast('gates', 'update', { message });
				return;
			}

			showMutationSuccessToast('gates', 'update');
			go({
				to: '/gates',
				state: { selectGateId: gate.terminalId }
			});
		} catch (error) {
			showMutationErrorToast('gates', 'update', { error });
		}
	};

	if (!id) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Gate not found</AlertTitle>
					<AlertDescription>
						A gate identifier was not provided in the URL.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (!isValidUlid(id)) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Invalid gate ID</AlertTitle>
					<AlertDescription>
						The provided identifier "{id}" is not a valid ULID.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (isGateLoading) {
		return (
			<FormPageLayout>
				<div className="space-y-4 text-sm text-muted-foreground">
					Loading gate details...
				</div>
			</FormPageLayout>
		);
	}

	if (gateError || !gate) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Unable to load gate</AlertTitle>
					<AlertDescription>
						{fetchError?.message ??
							'The requested gate could not be found.'}
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	return (
		<FormPageLayout>
			{terminalTypesError && (
				<Alert variant="destructive">
					<AlertTitle>Unable to load gate types</AlertTitle>
					<AlertDescription>
						{terminalTypesError.message}
					</AlertDescription>
				</Alert>
			)}

			<GateForm
				key={gate.terminalId}
				mode="edit"
				initialData={initialFormData}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				terminalTypeOptions={terminalTypeOptions}
				isTerminalTypeLoading={isTerminalTypeLoading}
				headerTitle={t('gates:edit.title')}
			/>
		</FormPageLayout>
	);
};
